/**
 * Collection hero behavior for sections/collection-hero.liquid:
 * - product count counts up on load
 * - description more/less toggle, only when the two-line clamp actually cuts
 * - accent derived from the collection artwork via the shared
 *   dominant-color sampler (--ch-accent + a contrast-safe --ch-accent-ink),
 *   with the accent setting as fallback
 *
 * Initializes every `[data-ix-colhero]` root on load and again on
 * `shopify:section:load` for theme-editor re-renders.
 */
import { sampleDominantColor } from '@theme/ix-accent';

function initHero(root) {
  if (!root || root.dataset.chInit) return;
  root.dataset.chInit = '1';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* product count counts up on load */
  const count = root.querySelector('[data-count]');
  if (count && !reduceMotion) {
    const target = parseInt(count.getAttribute('data-count'), 10);
    if (target > 0) {
      let start = null;
      const step = (ts) => {
        if (start == null) start = ts;
        const p = Math.min(1, (ts - start) / 700);
        const eased = 1 - Math.pow(1 - p, 3);
        count.textContent = '(' + Math.round(target * eased).toLocaleString('en-US') + ')';
        if (p < 1) requestAnimationFrame(step);
      };
      count.textContent = '(0)';
      requestAnimationFrame(step);
    }
  }

  /* description more/less toggle, only when the clamp actually cuts */
  const desc = root.querySelector('.ix-colhero__desc');
  const more = root.querySelector('.ix-colhero__more');
  if (desc && more) {
    const check = () => {
      if (!desc.classList.contains('is-clamped')) return;
      more.hidden = desc.scrollHeight <= desc.clientHeight + 2;
    };
    more.addEventListener('click', () => {
      const clamped = desc.classList.toggle('is-clamped');
      more.textContent = clamped ? 'more' : 'less';
    });
    check();
    if ('ResizeObserver' in window) new ResizeObserver(check).observe(desc);
  }

  /* accent derived from the collection artwork (pastelized dominant
     color, same approach as the ix banner carousel) */
  if (root.hasAttribute('data-auto-accent')) {
    const art = root.querySelector('.ix-colhero__img');
    if (art) {
      const derive = () => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const color = sampleDominantColor(img, 32);
          if (!color) return; // tainted canvas or too little signal: keep the fallback accent
          const r = color.r / 255;
          const g = color.g / 255;
          const b = color.b / 255;
          root.style.setProperty(
            '--ch-accent',
            'rgb(' + Math.round(r * 255) + ' ' + Math.round(g * 255) + ' ' + Math.round(b * 255) + ')'
          );
          // text needs contrast on white: keep the hue but force a
          // saturated, dark enough tone (pale pastels go muddy if
          // simply darkened)
          const mx = Math.max(r, g, b);
          const mn = Math.min(r, g, b);
          const l = (mx + mn) / 2;
          const dd = mx - mn;
          let h = 0;
          let sat = 0;
          if (dd > 0) {
            sat = dd / (1 - Math.abs(2 * l - 1));
            if (mx === r) h = ((g - b) / dd) % 6;
            else if (mx === g) h = (b - r) / dd + 2;
            else h = (r - g) / dd + 4;
            h = Math.round(h * 60);
            if (h < 0) h += 360;
          }
          const inkS = dd > 0 ? Math.max(45, Math.min(75, Math.round(sat * 100))) : 0;
          const inkL = Math.min(40, Math.round(l * 100));
          root.style.setProperty('--ch-accent-ink', 'hsl(' + h + ' ' + inkS + '% ' + inkL + '%)');
        };
        img.src = art.currentSrc || art.src;
      };
      if (art.complete) derive();
      else art.addEventListener('load', derive, { once: true });
    }
  }
}

function initAll(scope) {
  scope.querySelectorAll('[data-ix-colhero]').forEach(initHero);
}

initAll(document);
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
